import { Component, ErrorInfo, ReactNode } from "react";
import { AlertTriangle, Home, RefreshCw, Mail, ChevronDown } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { EventType, trackEvent } from "@/shared/utils/core";
import { sentryUtils } from "@/shared/utils/sentry";
import { getEnvironment } from "@/shared/hooks/useGetEnvironment";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

const isDev = getEnvironment() !== "prod";

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error("Error Boundary caught an error:", error, errorInfo);

    this.setState({
      error,
      errorInfo,
    });

    trackEvent(EventType.ERROR_ENCOUNTERED, {
      error_message: error.message,
      error_name: error.name,
      error_stack: error.stack?.substring(0, 500),
      component_stack: errorInfo.componentStack?.substring(0, 500),
      error_type: "react_error_boundary",
    });

    sentryUtils.captureError(error, "React error boundary", {
      componentStack: errorInfo.componentStack ?? undefined,
    });
  }

  handleReload = (): void => {
    window.location.reload();
  };

  handleGoHome = (): void => {
    window.location.href = "/";
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen bg-subtle flex items-center justify-center p-4">
          <div className="w-full max-w-2xl">
            <div className="bg-white rounded-2xl overflow-hidden">
              <div className="p-12 space-y-6">
                <div className="flex justify-center">
                  <div className="bg-red-50 rounded-full p-4">
                    <AlertTriangle className="w-10 h-10 text-red-500" strokeWidth={1.5} />
                  </div>
                </div>

                <div className="text-center space-y-3">
                  <h1>Something went wrong</h1>
                  <p className="text-secondary">
                    We encountered an unexpected error. Our team has been notified.
                  </p>
                </div>

                {isDev && this.state.error && (
                  <details className="group">
                    <summary className="cursor-pointer text-sm font-medium text-secondary hover:text-primary flex items-center gap-2">
                      Click to show stack trace (Local/Dev Only)
                      <ChevronDown size={16} />
                    </summary>
                    <div className="mt-4 p-4 bg-subtle rounded-lg border border-primary overflow-auto max-h-[300px]">
                      <p className="text-sm font-mono text-red-600 mb-2">
                        {this.state.error.toString()}
                      </p>
                      {this.state.errorInfo && (
                        <pre className="text-sm text-secondary overflow-x-auto whitespace-pre-wrap">
                          {this.state.errorInfo.componentStack}
                        </pre>
                      )}
                    </div>
                  </details>
                )}

                <div className="flex flex-col sm:flex-row gap-3 pt-4">
                  <Button onClick={this.handleReload} variant="brand" className="flex-1 h-12">
                    <RefreshCw className="w-4 h-4" />
                    Reload Page
                  </Button>
                  <Button onClick={this.handleGoHome} variant="outline" className="flex-1 h-12">
                    <Home className="w-4 h-4" />
                    Go to Home
                  </Button>
                </div>

                <div className="pt-6 border-t border-primary">
                  <div className="flex items-start gap-3 text-sm text-secondary">
                    <Mail className="w-5 h-5 text-tertiary flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium mb-1">Need help?</p>
                      <p>
                        If this problem persists, please contact our support team at{" "}
                        <a
                          href="mailto:support@elayne.com"
                          className="text-brand hover:underline font-medium"
                        >
                          support@elayne.com
                        </a>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="text-center mt-6 text-sm text-tertiary">
              <p>Error Code: {Date.now().toString(36).toUpperCase()}</p>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
